/**
 * Suggestion API calls
 */

import apiClient from './client';
import { getToken } from './client';
import {
  SuggestionRequest,
  SuggestionResponse,
  BulkAcceptRequest,
  BulkAcceptResponse,
  ReparseSuggestionsRequest,
  EntrySuggestion,
  MessageGap,
  SuggestionStats,
} from '@/types/api';

/**
 * Generate entry suggestions for a message range
 */
export const generateSuggestions = async (
  request: SuggestionRequest
): Promise<SuggestionResponse> => {
  const response = await apiClient.post<SuggestionResponse>('/suggestions/generate', request);
  return response.data;
};

/**
 * Get suggestion statistics (optional endpoint)
 */
export const getSuggestionStats = async (chatId: number): Promise<any> => {
  const response = await apiClient.get(`/suggestions/stats`, {
    params: { chat_id: chatId },
  });
  return response.data;
};

/**
 * Bulk accept suggestions
 */
export const bulkAcceptSuggestions = async (
  request: BulkAcceptRequest
): Promise<BulkAcceptResponse> => {
  const response = await apiClient.post<BulkAcceptResponse>('/suggestions/bulk-accept', request);
  return response.data;
};

/**
 * Re-parse suggestions using a forced pattern
 */
export const reparseSuggestions = async (
  request: ReparseSuggestionsRequest
): Promise<SuggestionResponse> => {
  const response = await apiClient.post<SuggestionResponse>('/suggestions/reparse', request);
  return response.data;
};

/**
 * Streaming callbacks for suggestion generation
 */
export interface StreamingCallbacks {
  onSuggestion: (suggestion: EntrySuggestion) => void;
  onGap: (gap: MessageGap) => void;
  onProgress: (data: { processed: number; total: number; percentage: number; message?: string }) => void;
  onComplete: (stats: SuggestionStats & { message: string }) => void;
  onError: (error: { message: string; code?: string }) => void;
}

/**
 * Stream entry suggestions using Server-Sent Events (SSE)
 *
 * @param request - Suggestion request parameters
 * @param callbacks - Event handlers for streaming events
 * @returns Cleanup function to close the EventSource connection
 */
export const streamSuggestions = (
  request: SuggestionRequest,
  callbacks: StreamingCallbacks
): (() => void) => {
  // Build URL with query parameters
  const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const params = new URLSearchParams({
    chat_id: request.chat_id.toString(),
    start_message_id: request.start_message_id.toString(),
    end_message_id: request.end_message_id.toString(),
    min_confidence: (request.min_confidence ?? 0.5).toString(),
  });

  // Get auth token
  const token = getToken();
  const url = `${baseURL}/suggestions/generate-stream?${params}${token ? `&token=${token}` : ''}`;

  // Create EventSource connection
  const eventSource = new EventSource(url);

  // Handle suggestion events
  eventSource.addEventListener('suggestion', (event: MessageEvent) => {
    try {
      const suggestion = JSON.parse(event.data) as EntrySuggestion;
      callbacks.onSuggestion(suggestion);
    } catch (error) {
      console.error('Error parsing suggestion event:', error);
    }
  });

  // Handle gap events
  eventSource.addEventListener('gap', (event: MessageEvent) => {
    try {
      const gap = JSON.parse(event.data) as MessageGap;
      callbacks.onGap(gap);
    } catch (error) {
      console.error('Error parsing gap event:', error);
    }
  });

  // Handle progress events
  eventSource.addEventListener('progress', (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      callbacks.onProgress(data);
    } catch (error) {
      console.error('Error parsing progress event:', error);
    }
  });

  // Handle completion event
  eventSource.addEventListener('complete', (event: MessageEvent) => {
    try {
      const stats = JSON.parse(event.data);
      callbacks.onComplete(stats);
      eventSource.close();
    } catch (error) {
      console.error('Error parsing complete event:', error);
      eventSource.close();
    }
  });

  // Handle error events
  eventSource.addEventListener('error', (event: MessageEvent) => {
    try {
      const errorData = JSON.parse(event.data);
      callbacks.onError(errorData);
    } catch (error) {
      // EventSource errors don't always have data
      callbacks.onError({ message: 'Connection error occurred' });
    } finally {
      eventSource.close();
    }
  });

  // Handle connection errors
  eventSource.onerror = (event) => {
    console.error('EventSource connection error:', event);
    callbacks.onError({ message: 'Connection to server lost' });
    eventSource.close();
  };

  // Return cleanup function
  return () => {
    if (eventSource.readyState !== EventSource.CLOSED) {
      eventSource.close();
    }
  };
};
